export type GridBoxPath = {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  color: string;
  index?: number; // Distance from start circle, used for backtracking validation
};

export type Point = { x: number; y: number };
export type ColourPoint = Point & { color: string };
export type SpecialTile = Point & { tileType: string; color?: string };
export type Grid = (string | null)[][];
export type World = {
  effects: string[];
  startingSize: number;
  startingColors: number;
  backgroundColour?: string; // Deprecated - use backgroundType instead
  backgroundType?: BackgroundType;
  name: string;
};

export enum GameMode {
  standard = "standard",
  classic = "classic",
  endless = "endless",
  adventure = "adventure",
  worldSelect = "worldSelect",
}

// Background types for different game modes/worlds
export enum BackgroundType {
  // Solid colors
  solidBlack = "solid-black",
  solidGreen = "solid-green",
  solidBrightGreen = "solid-bright-green",
  solidBrown = "solid-brown",
  solidPink = "solid-pink",
  solidGray = "solid-gray",
  solidDarkGray = "solid-dark-gray",
  solidRed = "solid-red",
  solidOrange = "solid-orange",
  solidCoral = "solid-coral",
  solidPurple = "solid-purple",
  solidViolet = "solid-violet",
  solidGold = "solid-gold",
  solidForest = "solid-forest",
  solidOcean = "solid-ocean",
  solidMagenta = "solid-magenta",

  // Animated/themed backgrounds
  calmWaves = "calm-waves",
  forestCanopy = "forest-canopy",
  desertDunes = "desert-dunes",
  portalRealm = "portal-realm",
  hauntedHouse = "haunted-house",
  vaporwaveCity = "vaporwave-city",
  alienVillage = "alien-village",
  paintSplat = "paint-splat",
  magicWorld = "magic-world",
  jungle = "jungle",
  technology = "technology",
  magicPortal = "magic-portal",
  warehouse = "warehouse",
  // Future backgrounds to add:
  // starfield = "starfield",
  // aurora = "aurora",
  // sunset = "sunset",
}

// Map background types to their CSS values (for solid colors)
export const backgroundStyles: Record<BackgroundType, string> = {
  [BackgroundType.solidBlack]: "black",
  [BackgroundType.solidGreen]: "#1B780F",
  [BackgroundType.solidBrightGreen]: "#20ab1b",
  [BackgroundType.solidBrown]: "#C19245",
  [BackgroundType.solidPink]: "#E043DD",
  [BackgroundType.solidGray]: "#343434",
  [BackgroundType.solidDarkGray]: "#606060",
  [BackgroundType.solidRed]: "#980000",
  [BackgroundType.solidOrange]: "#FF5733",
  [BackgroundType.solidCoral]: "#eb4034",
  [BackgroundType.solidPurple]: "#9900C3",
  [BackgroundType.solidViolet]: "#8C4BEC",
  [BackgroundType.solidGold]: "#FFC841",
  [BackgroundType.solidForest]: "#154511",
  [BackgroundType.solidOcean]: "#2783D8",
  [BackgroundType.solidMagenta]: "#9900C3",
  [BackgroundType.calmWaves]: "transparent", // Handled by component
  [BackgroundType.forestCanopy]: "transparent", // Handled by component
  [BackgroundType.desertDunes]: "transparent", // Handled by component
  [BackgroundType.portalRealm]: "transparent", // Handled by component
  [BackgroundType.hauntedHouse]: "transparent", // Handled by component
  [BackgroundType.vaporwaveCity]: "transparent", // Handled by component
  [BackgroundType.alienVillage]: "transparent", // Handled by component
  [BackgroundType.paintSplat]: "transparent", // Handled by component
  [BackgroundType.magicWorld]: "transparent", // Handled by component
  [BackgroundType.jungle]: "transparent", // Handled by component
  [BackgroundType.technology]: "transparent", // Handled by component
  [BackgroundType.magicPortal]: "transparent", // Handled by component
  [BackgroundType.warehouse]: "transparent", // Handled by component
};

export const levelTimerStart = 20;

export const allStageEffects = [
  "lock",
  "colour-spesific-tiles",
  "direction-spesific-tiles",
  "warp",
  "arrow-tiles",
  "dark",
  "bomb",
  "magic-box",
  "rotating-tiles",
  "summer",
  "painter-box",
  "zorbie",
];

export const colors = [
  "red",
  "green",
  "blue",
  "yellow",
  "purple",
  "orange",
  "cyan",
  "magenta",
  "lime",
  "pink",
  "teal",
  "lavender",
  "brown",
  "beige",
  "maroon",
  "mint",
  "olive",
  "coral",
  "navy",
];

export const iconColors = {
  red: "white",
  green: "white",
  blue: "white",
  yellow: "black",
  purple: "white",
  orange: "black",
  cyan: "black",
  magenta: "white",
  lime: "black",
  pink: "black",
  teal: "black",
  lavender: "black",
  brown: "white",
  beige: "black",
  maroon: "white",
  mint: "black",
  olive: "black",
  coral: "black",
  navy: "white",
};

export const unlockableStageTypes = [
  {
    level: 5,
    effect: "lock",
    popupText: "Unlock Locks with the Key!",
    color: "yellow",
  },
  {
    level: 10,
    effect: "colour-spesific-tiles",
    popupText: "Chroma-Set Tiles are Here!",
    color: "orange",
  },
  {
    level: 15,
    effect: "direction-spesific-tiles",
    popupText: "Tunnels are Here!",
    color: "blue",
  },
  {
    level: 20,
    effect: "warp",
    popupText: "It's Warp Time!",
    color: "pink",
  },
  {
    level: 25,
    effect: "arrow-tiles",
    popupText: "Arrow Tiles Arrive!",
    color: "green",
  },
  {
    level: 30,
    effect: "dark",
    popupText: "Lights Out!",
    color: "black",
  },
  {
    level: 35,
    effect: "bomb",
    popupText: "Tick Tock!",
    color: "red",
  },
  {
    level: 40,
    effect: "magic-box",
    popupText: "Magic Box!",
    color: "purple",
  },
  {
    level: 45,
    effect: "rotating-tiles",
    popupText: "Rotating Tiles!",
    color: "orange",
  },
  {
    level: 50,
    effect: "summer",
    popupText: "It's Summer!",
    color: "lime",
  },
  {
    level: 55,
    effect: "painter-box",
    popupText: "Get Painted!",
    color: "cyan",
  },
  {
    level: 60,
    effect: "zorbie",
    popupText: "Zorbies Arrive!",
    color: "magenta",
  },
];

export const worlds: World[] = [
  {
    effects: [],
    startingSize: 3,
    startingColors: 1,
    backgroundType: BackgroundType.solidBlack,
    name: "Basic",
  },
  {
    effects: ["lock", "arrow-tiles"],
    startingSize: 4,
    startingColors: 2,
    backgroundType: BackgroundType.forestCanopy,
    name: "Lock & Key",
  },
  {
    effects: [
      "colour-spesific-tiles",
      "direction-spesific-tiles",
      "rotating-tiles",
    ],
    startingSize: 4,
    startingColors: 3,
    backgroundType: BackgroundType.desertDunes,
    name: "Colours & Tunnels",
  },
  {
    effects: ["warp", "painter-box"],
    startingSize: 4,
    startingColors: 3,
    backgroundType: BackgroundType.portalRealm,
    name: "Warp",
  },
  {
    effects: ["dark", "colour-spesific-tiles", "zorbie"],
    startingSize: 5,
    startingColors: 4,
    backgroundType: BackgroundType.hauntedHouse,
    name: "Lights Out",
  },
  {
    effects: ["bomb"],
    startingSize: 4,
    startingColors: 2,
    backgroundType: BackgroundType.vaporwaveCity,
    name: "Tick Tock",
  },
  {
    effects: ["magic-box", "zorbie"],
    startingSize: 4,
    startingColors: 3,
    backgroundType: BackgroundType.alienVillage,
    name: "Magic Box",
  },
  {
    effects: ["warp", "direction-spesific-tiles", "painter-box"],
    startingSize: 5,
    startingColors: 4,
    backgroundType: BackgroundType.paintSplat,
    name: "Warp & Tunnels",
  },
  {
    effects: ["bomb", "magic-box", "arrow-tiles"],
    startingSize: 5,
    startingColors: 5,
    backgroundType: BackgroundType.magicWorld,
    name: "Bombs & Magic",
  },
  {
    effects: ["summer", "lock", "arrow-tiles", "direction-spesific-tiles"],
    startingSize: 5,
    startingColors: 4,
    backgroundType: BackgroundType.jungle,
    name: "Summer Locks",
  },
  {
    effects: [
      "lock",
      "arrow-tiles",
      "direction-spesific-tiles",
      "colour-spesific-tiles",
      "rotating-tiles",
    ],
    startingSize: 6,
    startingColors: 6,
    backgroundType: BackgroundType.technology,
    name: "Rotation & Locks",
  },
  {
    effects: ["warp", "magic-box", "painter-box", "arrow-tiles", "zorbie"],
    startingSize: 6,
    startingColors: 6,
    backgroundType: BackgroundType.magicPortal,
    name: "Magic & Mystery",
  },
];

export const customWorlds: World[] = [
  // Easy/Beginner worlds
  {
    effects: [],
    startingSize: 3,
    startingColors: 2,
    backgroundType: BackgroundType.calmWaves,
    name: "Calm Start",
  },
  {
    effects: ["arrow-tiles"],
    startingSize: 4,
    startingColors: 3,
    backgroundType: BackgroundType.solidGold,
    name: "Arrow Storm",
  },
  {
    effects: ["lock", "arrow-tiles", "colour-spesific-tiles"],
    startingSize: 3,
    startingColors: 2,
    backgroundType: BackgroundType.forestCanopy,
    name: "Locks & Arrows",
  },
  // Medium worlds
  {
    effects: ["warp", "magic-box"],
    startingSize: 4,
    startingColors: 3,
    backgroundType: BackgroundType.magicPortal,
    name: "Warp & Magic",
  },
  {
    effects: ["zorbie"],
    startingSize: 4,
    startingColors: 3,
    backgroundType: BackgroundType.alienVillage,
    name: "Zorbie Time",
  },
  {
    effects: ["warp", "colour-spesific-tiles"],
    startingSize: 4,
    startingColors: 4,
    backgroundType: BackgroundType.calmWaves,
    name: "Ocean Depths",
  },
  {
    effects: ["painter-box", "colour-spesific-tiles", "summer"],
    startingSize: 4,
    startingColors: 4,
    backgroundType: BackgroundType.paintSplat,
    name: "Paint Party",
  },
  {
    effects: ["dark", "direction-spesific-tiles", "arrow-tiles"],
    startingSize: 5,
    startingColors: 4,
    backgroundType: BackgroundType.hauntedHouse,
    name: "Haunted Maze",
  },
  // Hard worlds
  {
    effects: ["rotating-tiles", "magic-box"],
    startingSize: 5,
    startingColors: 6,
    backgroundType: BackgroundType.technology,
    name: "Logic & Locks",
  },
  {
    effects: ["bomb", "rotating-tiles"],
    startingSize: 5,
    startingColors: 4,
    backgroundType: BackgroundType.desertDunes,
    name: "Desert Rush",
  },
  {
    effects: ["bomb", "dark", "zorbie"],
    startingSize: 5,
    startingColors: 5,
    backgroundType: BackgroundType.vaporwaveCity,
    name: "Neon Nightmare",
  },
  // Expert worlds
  {
    effects: [],
    startingSize: 7,
    startingColors: 12,
    backgroundType: BackgroundType.warehouse,
    name: "Packed",
  },
  {
    effects: ["warp", "magic-box", "painter-box", "zorbie", "rotating-tiles"],
    startingSize: 6,
    startingColors: 6,
    backgroundType: BackgroundType.portalRealm,
    name: "Portal Chaos",
  },
  {
    effects: allStageEffects,
    startingSize: 6,
    startingColors: 8,
    backgroundType: BackgroundType.magicWorld,
    name: "Everything!",
  },
];
